import {
    AudioOutlined,
    BookOutlined,
    DeleteOutlined,
    EditOutlined,
    EyeOutlined,
    PlusOutlined,
    StopOutlined
} from "@ant-design/icons";
import { Button, Input, notification, Popconfirm, Select, Space, Tooltip } from "antd";
import Modal from "antd/lib/modal/Modal";
import { ColumnType } from "antd/lib/table";
import axios from "axios";
import { motion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IBook, IGetBooksRequest } from "../../common/book.interface";
import CModalEditBook from "../../components/modalEditBook/CModalEditBook";
import CModalAddFile from "../../components/modalEditBook/CModalAddFile";
import CModalUserList from "../../components/modalEditBook/CModalUserList";
import AudioDetailModal from "../../components/audio-detail/AudioDetailModal";
import CTable from "../../components/table/CTable";
import { QUERY_PARAM } from "../../constants/get-api.constant";
import { API_URL } from "../../enums/api.enums";
import {
    deleteBookRequest,
    getBookRequest,
    getCategoryRequest,
    getDetailBookRequests
} from "../../redux/controller";
import { useDispatchRoot, useSelectorRoot } from "../../redux/store";
import Utils from "../../utils/base-utils";
import "./book.styles.scss";

const columns: ColumnType<IBook>[] = [
    {
        title: "Số thứ tự",
        dataIndex: "index",
        key: "index",
        width: "120px",
    },
    {
        title: "Tên Sách",
        dataIndex: "name",
        key: "name",
    },
    {
        title: "Tác Giả",
        dataIndex: "author",
        key: "author",
    },
    {
        title: "Thời gian tạo",
        dataIndex: "createdAt",
        key: "createdAt",
    },
    {
        title: "Giá",
        dataIndex: "price",
        key: "price",
    },
    {
        title: "Ảnh Bìa",
        dataIndex: "cover",
        key: "cover",
    },
    {
        title: "Trạng thái",
        dataIndex: "status",
        key: "status",
    },
    {
        title: "Số lượng mua",
        dataIndex: "userCount",
        key: "userCount",
    },
    {
        title: 'Số lượng yêu thích',
        dataIndex: 'favoriteCount',
        key: 'favoriteCount',
    },
    {
        title: "Thao tác",
        key: "action",
        dataIndex: "action",
    },
];

const Book = () => {
    const Option = Select;
    const {
        bookList,
        totalBookRecords,
        detailBook,
        loading,
    } = useSelectorRoot((state) => state.management);

    const [textSearch, setTextSearch] = useState("");
    const [openModal, setOpenModal] = useState(false);
    const [openModalEditBook, setOpenModalEditBook] = useState(false);
    const [selectedEditBook, setSelectedEditBook] = useState<any>(null);
    const [currentSearchValue, setCurrentSearchValue] =
        useState<IGetBooksRequest>({ limit: QUERY_PARAM.size, page: 1 });

    const dispatch = useDispatchRoot();
    const navigate = useNavigate();

    const [idBook, setIdBook] = useState("");
    const [lstBook, setLstBook] = useState<any[]>([]);
    const [lstUserBook, setLstUserBook] = useState<any[]>([]);
    const [totalUserBookRecords, setTotalUserBookRecords] = useState(0);
    const [isOpenModalUserBook, setIsOpenModalUserBook] = useState(false);
    const [isOpenModalAudio, setIsOpenModalAudio] = useState(false);
    const [lstAudioProcessing, setLstAudioProcessing] = useState<any[]>([]);
    const [isOpenModalSummary, setIsOpenModalSummary] = useState(false);
    const [lstSummaryProcessing, setLstSummaryProcessing] = useState<any[]>([]);
    const [primaryVoice, setPrimaryVoice] = useState<any[]>([]);
    const [lstAudio, setLstAudio] = useState<any[]>([]);
    const [deletingBookId, setDeletingBookId] = useState<string | null>(null);
    const [refreshKey, setRefreshKey] = useState<number>(0);
    const [imageRefreshTimestamp, setImageRefreshTimestamp] = useState<number>(Date.now());
    const [isAddVoiceVisible, setIsAddVoiceVisible] = useState(false);
    const [selectedBookForVoice, setSelectedBookForVoice] = useState<any>(null);
    const [voiceList, setVoiceList] = useState<any[]>([]);
    const [selectedVoiceIds, setSelectedVoiceIds] = useState<string[]>([]);
    const [synthesizing, setSynthesizing] = useState(false);

    // Bulk action modal
    const [isBulkActionVisible, setIsBulkActionVisible] = useState(false);
    const [bulkSelectedBookIds, setBulkSelectedBookIds] = useState<string[]>([]);
    const [bulkActionType, setBulkActionType] = useState<'add' | 'delete'>('add');
    const [bulkVoiceList, setBulkVoiceList] = useState<any[]>([]);
    const [bulkSelectedVoiceIds, setBulkSelectedVoiceIds] = useState<string[]>([]);
    const [bulkTasksList, setBulkTasksList] = useState<any[]>([]);
    const [bulkSelectedTaskIds, setBulkSelectedTaskIds] = useState<string[]>([]);
    const [bulkProcessing, setBulkProcessing] = useState(false);
    const [bulkStep, setBulkStep] = useState<'select' | 'action'>('select');
    const [bulkBookSearch, setBulkBookSearch] = useState('');
    const [bulkAllBooks, setBulkAllBooks] = useState<any[]>([]);
    const [bulkLoading, setBulkLoading] = useState(false);

    // Audio detail modal
    const [audioDetailVisible, setAudioDetailVisible] = useState(false);
    const [selectedTaskForDetail, setSelectedTaskForDetail] = useState<{ id: string; voiceName: string } | null>(null);

    const bookListRef = useRef(bookList);
    bookListRef.current = bookList;

    // Cache: bookId -> { hasAudio: boolean, lastFetchTime }
    // hasAudio=false means tasks API returned no tasks → don't re-fetch aggressively
    // hasAudio=true means tasks exist → keep polling only while tasks are active
    const audioStatusCacheRef = useRef<Map<string, { hasAudio: boolean; lastFetch: number; hasProcessingTask?: boolean }>>(new Map());
    // Supplement data cache: bookId -> { statusJSX, userCount, favoriteCount }
    const supplementCacheRef = useRef<Map<string, { statusJSX: any; userCount: number; favoriteCount: number; userBuyBookData?: any }>>(new Map());
    const isRefreshingBooksRef = useRef(false);
    const pendingRefreshRef = useRef(false);
    const activeRefreshTokenRef = useRef(0);

    const TASK_REFRESH_INTERVAL_MS = 15000;
    const TASK_ACTIVE_CACHE_MS = 12000;
    const TASK_INACTIVE_CACHE_MS = 5 * 60 * 1000;
    const SUPPLEMENT_TIMEOUT_MS = 4000;

    const getAuthorName = (record: any) => {
        if (record.name) return record.name;
        if (record.firstName || record.lastName) {
            const firstName = record.firstName || '';
            const middleName = record.middleName || '';
            const lastName = record.lastName || '';
            return [firstName, middleName, lastName].filter(Boolean).join(' ');
        }
        return 'Chưa có tên';
    };

    const getToken = () => {
        let token: any = localStorage.getItem("token");
        return token ? token.replace(/['"]+/g, '') : '';
    };

    const handleCancelTask = async (taskId: string, voiceName: string) => {
        try {
            await axios.delete(`${API_URL.HOST_UPLOAD_VOICE}/task/${taskId}`);
            notification.success({
                message: 'Hủy xử lý thành công',
                description: `Đã dừng xử lý audio: ${voiceName}`,
                placement: 'topRight'
            });
            // Refresh cache và data
            supplementCacheRef.current.clear();
            refreshAllBooksData();
        } catch (error) {
            console.error('Error cancelling task:', error);
            notification.error({
                message: 'Lỗi khi hủy xử lý',
                description: 'Không thể hủy task. Vui lòng thử lại.',
                placement: 'topRight'
            });
        }
    };

    const handleOpenAddVoice = async (book: any) => {
        setSelectedBookForVoice(book);
        setSelectedVoiceIds([]);
        setIsAddVoiceVisible(true);
        try {
            const res = await axios.get(`${API_URL.HOST_UPLOAD_VOICE}/list-voices`);
            setVoiceList(res.data?.data || res.data || []);
        } catch {
            notification.error({ message: "Không thể tải danh sách giọng" });
            setIsAddVoiceVisible(false);
        }
    };

    const handleSynthesizeNewVoice = async () => {
        if (!selectedBookForVoice || selectedVoiceIds.length === 0) return;
        setSynthesizing(true);
        try {
            const results = await Promise.allSettled(
                selectedVoiceIds.map(async (voiceId: string) => {
                    const body = new URLSearchParams({
                        book_id: selectedBookForVoice.id.toString(),
                        voice_id: voiceId,
                    });
                    const res = await fetch(`${API_URL.HOST_UPLOAD_VOICE}/synthesize-book`, {
                        method: "POST",
                        headers: { "Content-Type": "application/x-www-form-urlencoded" },
                        body,
                    });
                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                    return res.json();
                })
            );
            const success = results.filter(r => r.status === "fulfilled").length;
            const fail = results.filter(r => r.status === "rejected").length;
            notification.success({
                message: "Tạo giọng đọc thành công",
                description: `${success} giọng đã bắt đầu xử lý${fail > 0 ? `, ${fail} giọng lỗi` : ""}`,
            });
            setIsAddVoiceVisible(false);
            supplementCacheRef.current.delete(selectedBookForVoice.id.toString());
            refreshAllBooksData();
        } catch {
            notification.error({ message: "Lỗi khi tạo giọng đọc" });
        }
        setSynthesizing(false);
    };

    const getSelectedBooks = () => {
        return bulkAllBooks.filter((book: any) => bulkSelectedBookIds.includes(book.id?.toString()));
    };

    const handleOpenBulkAction = async () => {
        setBulkSelectedBookIds([]);
        setBulkActionType('add');
        setBulkSelectedVoiceIds([]);
        setBulkSelectedTaskIds([]);
        setBulkTasksList([]);
        setBulkStep('select');
        setBulkBookSearch('');
        setBulkLoading(true);
        setIsBulkActionVisible(true);
        try {
            const [voicesRes, booksRes] = await Promise.all([
                axios.get(`${API_URL.HOST_UPLOAD_VOICE}/list-voices`),
                axios.get(`${API_URL.HOST}/books/all`),
            ]);
            setBulkVoiceList(voicesRes.data?.data || voicesRes.data || []);
            const apiRes = booksRes.data;
            const books = apiRes?.data || (Array.isArray(apiRes) ? apiRes : []);
            setBulkAllBooks(Array.isArray(books) ? books : []);
        } catch {
            notification.error({ message: "Không thể tải danh sách sách" });
        }
        setBulkLoading(false);
    };

    const handleBulkGoToAction = async () => {
        if (bulkSelectedBookIds.length === 0) return;
        setBulkStep('action');
        if (bulkActionType === 'delete') {
            setBulkSelectedTaskIds([]);
            const selectedBooks = getSelectedBooks();
            const allTasks: any[] = [];
            const results = await Promise.allSettled(
                selectedBooks.map(async (book: any) => {
                    const res = await axios.get(`${API_URL.HOST_UPLOAD_VOICE}/book/${book.id}/tasks`);
                    const tasks = (res.data?.tasks || []).map((t: any) => ({
                        ...t,
                        bookName: book.name || book.title,
                        bookId: book.id,
                    }));
                    return tasks;
                })
            );
            results.forEach((r) => {
                if (r.status === "fulfilled") allTasks.push(...r.value);
            });
            setBulkTasksList(allTasks);
        }
    };

    const handleBulkExecute = async () => {
        if (bulkActionType === 'add') {
            if (bulkSelectedVoiceIds.length === 0) return;
            setBulkProcessing(true);
            const selectedBooks = getSelectedBooks();
            let success = 0;
            let fail = 0;
            for (const book of selectedBooks) {
                const results = await Promise.allSettled(
                    bulkSelectedVoiceIds.map(async (voiceId: string) => {
                        const body = new URLSearchParams({
                            book_id: book.id.toString(),
                            voice_id: voiceId,
                        });
                        const res = await fetch(`${API_URL.HOST_UPLOAD_VOICE}/synthesize-book`, {
                            method: "POST",
                            headers: { "Content-Type": "application/x-www-form-urlencoded" },
                            body,
                        });
                        if (!res.ok) throw new Error(`HTTP ${res.status}`);
                        return res.json();
                    })
                );
                success += results.filter(r => r.status === "fulfilled").length;
                fail += results.filter(r => r.status === "rejected").length;
                supplementCacheRef.current.delete(book.id.toString());
            }
            notification.success({
                message: "Tạo giọng đọc hàng loạt thành công",
                description: `${success} giọng đã bắt đầu xử lý trên ${selectedBooks.length} sách${fail > 0 ? `, ${fail} lỗi` : ""}`,
            });
            setIsBulkActionVisible(false);
            refreshAllBooksData();
            setBulkProcessing(false);
        } else {
            if (bulkSelectedTaskIds.length === 0) return;
            setBulkProcessing(true);
            const results = await Promise.allSettled(
                bulkSelectedTaskIds.map(async (taskId: string) => {
                    await axios.delete(`${API_URL.HOST_UPLOAD_VOICE}/task/${taskId}`);
                })
            );
            const success = results.filter(r => r.status === "fulfilled").length;
            const fail = results.filter(r => r.status === "rejected").length;
            notification.success({
                message: "Xóa giọng đọc thành công",
                description: `Đã xóa ${success} task${fail > 0 ? `, ${fail} task lỗi` : ""}`,
            });
            setIsBulkActionVisible(false);
            supplementCacheRef.current.clear();
            refreshAllBooksData();
            setBulkProcessing(false);
        }
    };

    const renderStatusColumn = (allTasks: any[], allProgressBooks: any[], bookId?: string) => {
        if (!allTasks || allTasks.length === 0) {
            return <div style={{ color: "red" }}>Chưa có file sách</div>;
        }
        return (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", flexWrap: "wrap" }}>
                <Tooltip placement="top" title={"Đã có file ebook"}>
                    <div style={{ marginRight: "10px", color: "green" }}>
                        <BookOutlined />
                    </div>
                </Tooltip>
                {allTasks.map((task: any, taskIndex: number) => {
                    const progressBook = allProgressBooks[taskIndex] || {};
                    const voiceName = task.voice_name || task.voiceId || `Task ${taskIndex + 1}`;
                    const taskStatus = task.status || progressBook.status;
                    const percent = progressBook.percent || 0;
                    const totalChapters = progressBook.totalChapters || 0;
                    const currentChapter = progressBook.chapter || 0;

                    return (
                        <div key={taskIndex} style={{ display: "flex", alignItems: "center", marginRight: "10px", marginBottom: "5px" }}>
                            <div style={{ marginRight: "5px" }}>
                                {taskStatus === 'queued' || taskStatus === 'pending' ? (
                                    <Tooltip placement="top" title={`${voiceName}: Đang chờ xử lý`}>
                                        <div style={{ color: 'orange', cursor: 'pointer' }}>
                                            <AudioOutlined />
                                            <span style={{ marginLeft: 2, fontSize: 10 }}>Chờ</span>
                                        </div>
                                    </Tooltip>
                                ) : taskStatus === 'failed' ? (
                                    <Tooltip placement="top" title={`${voiceName}: Lỗi xử lý${progressBook.error ? ` - ${progressBook.error}` : ''}`}>
                                        <div style={{ color: 'red', cursor: 'pointer' }}>
                                            <AudioOutlined />
                                            <span style={{ marginLeft: 2, fontSize: 10 }}>Lỗi</span>
                                        </div>
                                    </Tooltip>
                                ) : taskStatus === 'done' || percent === 100 ? (
                                    <Tooltip placement="top" title={`${voiceName}: Đã xử lý xong (${totalChapters} chương) - Bấm để nghe`}>
                                        <div
                                            style={{ color: "green", cursor: 'pointer' }}
                                            onClick={() => {
                                                setSelectedTaskForDetail({ id: task.id, voiceName });
                                                setAudioDetailVisible(true);
                                            }}
                                        >
                                            <AudioOutlined />
                                            <span style={{ marginLeft: 2, fontSize: 10 }}>Xong</span>
                                        </div>
                                    </Tooltip>
                                ) : taskStatus === 'processing' ? (
                                    <Tooltip placement="top" title={`${voiceName}: Đang xử lý - Chương ${currentChapter}/${totalChapters} (${percent}%)`}>
                                        <div style={{ color: '#1890ff', cursor: 'pointer' }}>
                                            <AudioOutlined />
                                            <span style={{ marginLeft: 2, fontSize: 10 }}>{percent}%</span>
                                        </div>
                                    </Tooltip>
                                ) : (
                                    <Tooltip placement="top" title={`${voiceName}: Chưa bắt đầu xử lý`}>
                                        <div style={{ color: 'gray', cursor: 'pointer' }}>
                                            <AudioOutlined />
                                            <span style={{ marginLeft: 2, fontSize: 10 }}>N/A</span>
                                        </div>
                                    </Tooltip>
                                )}
                            </div>
                            {/* Nút hủy task đang xử lý */}
                            {(taskStatus === 'processing' || taskStatus === 'queued' || taskStatus === 'pending') && (
                                <Popconfirm
                                    title={`Hủy xử lý ${voiceName}?`}
                                    onConfirm={() => handleCancelTask(task.id, voiceName)}
                                    okText="Hủy"
                                    cancelText="Giữ lại"
                                    okType="danger"
                                >
                                    <Tooltip placement="top" title="Hủy xử lý">
                                        <StopOutlined
                                            style={{ color: '#ff4d4f', cursor: 'pointer', fontSize: 12, marginLeft: 4 }}
                                        />
                                    </Tooltip>
                                </Popconfirm>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    const buildBasicBookRow = (book: any, index: number) => {
        return {
            index: currentSearchValue.limit * ((currentSearchValue.page || 1) - 1) + index + 1,
            id: book.id,
            name: book.name || book.title,
            author: book.authorsBooks && book.authorsBooks.length > 0
                ? book.authorsBooks.map((authorBook: any) => getAuthorName(authorBook.author)).join(", ")
                : (book.authors && book.authors.length > 0
                    ? book.authors.map((author: any) => getAuthorName(author)).join(", ")
                    : "Không có tác giả"),
            createdAt: new Date(book.createdAt).toLocaleDateString("en-GB"),
            price: Utils.formatMoney(book.pricePaper || book.price || 0) + "VNĐ",
            cover: (
                <img
                    style={{ width: "90px", height: "110px" }}
                    src={`${API_URL.HOST}/books/preview-images?id=${book.id}&t=${imageRefreshTimestamp}`}
                    alt=""
                />
            ),
            status: (
                <div style={{ color: "red" }}>Chưa có file sách</div>
            ),
            userCount: 0,
            favoriteCount: 0,
            action: (
                <Space size="middle" style={{ display: "flex", justifyContent: "center" }}>
                    <Tooltip placement="top" title={"Chỉnh Sửa"}>
                        <EditOutlined onClick={() => handleEditBook(book)} />
                    </Tooltip>
                    <Tooltip placement="top" title={"Thêm file sách"}>
                        <BookOutlined onClick={() => handleDetail(book)} />
                    </Tooltip>
                    <Tooltip placement="top" title={"Thêm giọng đọc"}>
                        <PlusOutlined
                            style={{ color: '#1890ff' }}
                            onClick={() => handleOpenAddVoice(book)}
                        />
                    </Tooltip>
                    <Tooltip placement="top" title={"Xem người dùng đã mua"}>
                        <EyeOutlined onClick={() => handleViewUserBook({ items: [], count: 0 })} />
                    </Tooltip>
                    <Popconfirm
                        title="Bạn có chắc chắn muốn xóa sách này?"
                        onConfirm={() => handleDelete(book)}
                        okText="Xóa"
                        cancelText="Hủy"
                        okType="danger"
                        disabled={deletingBookId === book.id}
                    >
                        <Tooltip placement="top" title={"Xóa sách"}>
                            <DeleteOutlined
                                style={{
                                    color: deletingBookId === book.id ? '#ccc' : '#ff4d4f',
                                    cursor: deletingBookId === book.id ? 'not-allowed' : 'pointer',
                                    opacity: deletingBookId === book.id ? 0.5 : 1
                                }}
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            )
        };
    };

    const fetchBookSupplementData = async (book: any, bookIndex: number) => {
        const bookId = book.id?.toString();
        const cache = audioStatusCacheRef.current;
        const cached = cache.get(bookId);
        const now = Date.now();

        // Timeout wrapper to prevent hanging
        const withTimeout = <T,>(promise: Promise<T>, ms: number): Promise<T> =>
            Promise.race([promise, new Promise<T>((_, reject) =>
                setTimeout(() => reject(new Error('timeout')), ms)
            )]);

        const emptyResult = {
            userBuyBookCount: 0,
            userBuyBookData: { items: [], count: 0 },
            favoriteCount: 0,
            statusJSX: <div style={{ color: "red" }}>Chưa có file sách</div>,
            allTasks: [],
            hasProcessingTask: false,
        };

        if (cached && !cached.hasAudio && (now - cached.lastFetch) < TASK_INACTIVE_CACHE_MS) {
            return emptyResult;
        }

        const tasksResult = await withTimeout(
            axios.get(`${API_URL.HOST_UPLOAD_VOICE}/book/${book.id}/tasks`)
                .then(r => r.data)
                .catch(() => ({ tasks: [] })),
            SUPPLEMENT_TIMEOUT_MS
        ).catch(() => ({ tasks: [] }));

        let allTasks: any[] = tasksResult?.tasks || [];
        let allProgressBooks: any[] = [];
        let statusJSX;

        if (allTasks.length > 0) {
            allProgressBooks = allTasks.map((task: any) => {
                try {
                    return typeof task.progress === "string" ? JSON.parse(task.progress) : task.progress;
                } catch (e) {
                    return {};
                }
            });
            statusJSX = renderStatusColumn(allTasks, allProgressBooks, book.id);
        } else {
            statusJSX = <div style={{ color: "red" }}>Chưa có file sách</div>;
        }

        const hasProcessingTask = allTasks.some((t: any) =>
            t.status === 'processing' || t.status === 'queued' || t.status === 'pending'
        );

        cache.set(bookId, { hasAudio: allTasks.length > 0, lastFetch: now, hasProcessingTask });

        const result = {
            userBuyBookCount: 0,
            userBuyBookData: { items: [], count: 0 },
            favoriteCount: 0,
            statusJSX,
            allTasks,
            hasProcessingTask,
        };

        supplementCacheRef.current.set(bookId, {
            statusJSX,
            userCount: 0,
            favoriteCount: 0,
            userBuyBookData: { items: [], count: 0 },
        });

        return result;
    };

    const refreshAllBooksData = useCallback(async () => {
        if (isRefreshingBooksRef.current) {
            pendingRefreshRef.current = true;
            return;
        }

        const books = bookListRef.current;
        if (!books || books.length === 0) {
            setLstBook([]);
            return;
        }

        isRefreshingBooksRef.current = true;
        pendingRefreshRef.current = false;
        const refreshToken = ++activeRefreshTokenRef.current;

        try {
            const cache = audioStatusCacheRef.current;
            const now = Date.now();

            const rows = await Promise.all(books.map(async (book: any, index: number) => {
                const bookId = book.id?.toString();
                const cached = cache.get(bookId);
                const supplementCached = supplementCacheRef.current.get(bookId);
                const basicRow = buildBasicBookRow(book, index);
                const cacheAge = cached ? now - cached.lastFetch : Infinity;
                const shouldFetch = !cached
                    || (cached.hasProcessingTask && cacheAge > TASK_ACTIVE_CACHE_MS)
                    || (!cached.hasAudio && cacheAge > TASK_INACTIVE_CACHE_MS);

                if (!shouldFetch && supplementCached) {
                    return {
                        ...basicRow,
                        status: supplementCached.statusJSX,
                        userCount: supplementCached.userCount,
                        favoriteCount: supplementCached.favoriteCount,
                    };
                }

                if (!shouldFetch) {
                    return basicRow;
                }

                try {
                    const supplement = await fetchBookSupplementData(book, index);
                    return {
                        ...basicRow,
                        status: supplement.statusJSX,
                        userCount: supplement.userBuyBookCount,
                        favoriteCount: supplement.favoriteCount,
                    };
                } catch {
                    return supplementCached ? {
                        ...basicRow,
                        status: supplementCached.statusJSX,
                        userCount: supplementCached.userCount,
                        favoriteCount: supplementCached.favoriteCount,
                    } : basicRow;
                }
            }));

            if (refreshToken === activeRefreshTokenRef.current) {
                setLstBook(rows);
            }
            return rows;
        } finally {
            isRefreshingBooksRef.current = false;
            if (pendingRefreshRef.current) {
                pendingRefreshRef.current = false;
                setTimeout(() => refreshAllBooksData(), 0);
            }
        }
    }, [currentSearchValue, imageRefreshTimestamp, deletingBookId]);

    useEffect(() => {
        // Clear cache when search/page changes
        audioStatusCacheRef.current.clear();
        supplementCacheRef.current.clear();
        const fetchInitialData = async () => {
            await dispatch(getBookRequest(currentSearchValue));
            dispatch(getCategoryRequest());
            getAllPrimaryVoice();
        };
        fetchInitialData();
    }, [currentSearchValue.page, currentSearchValue.limit, currentSearchValue.search]);

    useEffect(() => {
        if (!bookList || bookList.length === 0) {
            setLstBook([]);
            return;
        }
        refreshAllBooksData();
    }, [bookList]);

    useEffect(() => {
        let interval: NodeJS.Timeout | null = null;
        const hasActiveTask = Array.from(audioStatusCacheRef.current.values()).some(
            (item) => item.hasProcessingTask
        );

        if (bookList && bookList.length > 0 && hasActiveTask) {
            interval = setInterval(() => {
                refreshAllBooksData();
            }, TASK_REFRESH_INTERVAL_MS);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [bookList?.length, lstBook]);

    useEffect(() => {
        if (refreshKey > 0) {
            refreshAllBooksData();
        }
    }, [refreshKey]);

    const onChangeInput = (event: any) => {
        setTextSearch(event.target.value);
    };

    const onSearch = () => {
        const body: IGetBooksRequest = {
            limit: QUERY_PARAM.size,
            page: 1,
            search: textSearch,
        };
        const finalBody = Utils.getRidOfUnusedProperties(body);
        setCurrentSearchValue(finalBody);
    };

    const onChangePagination = (event: any) => {
        setCurrentSearchValue({ ...currentSearchValue, page: event });
    };

    const handleDetail = async (record: any) => {
        setIdBook(record.id);
        setOpenModal(true);
    };

    const handleEditBook = (record: any) => {
        setSelectedEditBook(record);
        setOpenModalEditBook(true);
        dispatch(getDetailBookRequests(record.id));
    };

    const handleDelete = async (record: any) => {
        const bodyRequest = { id: record.id };
        setDeletingBookId(record.id);

        try {
            await dispatch(deleteBookRequest(bodyRequest));
            notification.success({
                message: 'Xóa sách thành công',
                description: 'Sách đã được xóa khỏi hệ thống',
                placement: 'topRight'
            });
            await dispatch(getBookRequest(currentSearchValue));
            setRefreshKey(prev => prev + 1);
        } catch (error) {
            console.error("Error deleting book:", error);
            notification.error({
                message: 'Lỗi khi xóa sách',
                description: 'Có lỗi xảy ra khi xóa sách. Vui lòng thử lại.',
                placement: 'topRight'
            });
        } finally {
            setDeletingBookId(null);
        }
    };

    const handleFinishAddFile = async (values: any) => {
        try {
            const formData = new FormData();
            formData.append("epubFile", values.fileUploadLst[0]);
            formData.append("bookId", idBook);
            const res = await fetch(`${API_URL.HOST}/books/upload-epub`, {
                method: "POST",
                body: formData,
            });
            const data = await res.json();
            console.log("data", data);

            const formData2 = new FormData();
            formData2.append("epub_file", values.fileUploadLst[0]);
            formData2.append("book_id", idBook);
            try {
                const res2 = await fetch(`${API_URL.HOST_UPLOAD_VOICE}/upload-book`, {
                    method: "POST",
                    body: formData2,
                });
                const data2 = await res2.json();
                console.log("data2", data2);
            } catch (error) {
                console.log("error", error);
            }

            if (values.selectVoice && values.selectVoice.length > 0) {
                const synthesizePromises = values.selectVoice.map(async (voiceId: any) => {
                    try {
                        const body3 = new URLSearchParams({
                            book_id: idBook,
                            voice_id: voiceId,
                        });
                        const res3 = await fetch(`${API_URL.HOST_UPLOAD_VOICE}/synthesize-book`, {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/x-www-form-urlencoded",
                            },
                            body: body3,
                        });
                        const data3 = await res3.json();
                        return { voiceId, success: true, data: data3 };
                    } catch (error) {
                        return { voiceId, success: false, error };
                    }
                });

                try {
                    const results = await Promise.all(synthesizePromises);
                    const successCount = results.filter(result => result.success).length;
                    const failCount = results.filter(result => !result.success).length;

                    if (failCount > 0) {
                        notification.warning({
                            message: "Một số giọng đọc không thể xử lý",
                            description: `${successCount} giọng đọc đã được tạo thành công, ${failCount} giọng đọc gặp lỗi`,
                        });
                    }
                } catch (error) {
                    notification.error({
                        message: "Lỗi khi tạo giọng đọc",
                        description: "Có lỗi xảy ra trong quá trình tạo giọng đọc",
                    });
                }
            }

            setImageRefreshTimestamp(Date.now());
            notification.success({
                message: "Thêm file sách thành công",
                description: `File sách đã được tải lên và bắt đầu tạo ${values.selectVoice?.length || 0} giọng đọc`,
            });

            // Clear cache for this book so tasks API gets called
            audioStatusCacheRef.current.delete(idBook?.toString());
            supplementCacheRef.current.delete(idBook?.toString());

            await dispatch(getBookRequest(currentSearchValue));
            setTimeout(() => {
                refreshAllBooksData();
            }, 500);
        } catch (err) {
            console.log(err);
            throw err;
        }
    };

    const handleViewUserBook = (record: any) => {
        setLstUserBook(record.items);
        setTotalUserBookRecords(record.count);
        setIsOpenModalUserBook(true);
    };

    const handleOpenModalAudio = async (lstAudio: any, lstNewAduio: any) => {
        setIsOpenModalAudio(true);
        setLstAudioProcessing([...lstAudio, ...lstNewAduio]);
    };

    const handleOpenModalSummary = async (lstSummary: any) => {
        setIsOpenModalSummary(true);
        setLstSummaryProcessing(lstSummary);
    };

    const getAllPrimaryVoice = async () => {
        await axios.get(`${API_URL.HOST_UPLOAD_VOICE}/${API_URL.GET_ALL_PRIMARY_VOICE}`)
            .then((res) => {
                setPrimaryVoice(res.data);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    return (
        <motion.div
            className="sketch-main"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
        >
            {openModal && (
                <CModalAddFile
                    open={openModal}
                    bookId={idBook}
                    setOpenModal={setOpenModal}
                    onFinish={handleFinishAddFile}
                    primaryVoice={primaryVoice}
                />
            )}
            {openModalEditBook && selectedEditBook && (
                <CModalEditBook
                    open={openModalEditBook}
                    data={detailBook?.id === selectedEditBook?.id ? detailBook : selectedEditBook}
                    isDetailLoading={loading && detailBook?.id !== selectedEditBook?.id}
                    setOpenModalEdit={(open) => {
                        setOpenModalEditBook(open);
                        if (!open) setSelectedEditBook(null);
                    }}
                    onUpdated={async () => {
                        audioStatusCacheRef.current.clear();
                        supplementCacheRef.current.clear();
                        await dispatch(getBookRequest(currentSearchValue));
                        setRefreshKey(prev => prev + 1);
                        setTimeout(() => {
                            refreshAllBooksData();
                        }, 300);
                    }}
                    layout="single"
                />
            )}
            {selectedTaskForDetail && (
                <AudioDetailModal
                    open={audioDetailVisible}
                    taskId={selectedTaskForDetail.id}
                    voiceName={selectedTaskForDetail.voiceName}
                    onClose={() => {
                        setAudioDetailVisible(false);
                        setSelectedTaskForDetail(null);
                    }}
                />
            )}
            <Modal
                title={`Thêm giọng đọc cho "${selectedBookForVoice?.name || ''}"`}
                open={isAddVoiceVisible}
                onCancel={() => setIsAddVoiceVisible(false)}
                onOk={handleSynthesizeNewVoice}
                okText={synthesizing ? "Đang xử lý..." : "Tạo audio"}
                cancelText="Hủy"
                okButtonProps={{ disabled: selectedVoiceIds.length === 0 || synthesizing, loading: synthesizing }}
                width={500}
            >
                <div style={{ marginBottom: 8, color: '#666' }}>
                    Chọn giọng đọc muốn thêm cho sách này. File epub đã có sẵn trên hệ thống.
                </div>
                <Select
                    mode="multiple"
                    style={{ width: "100%" }}
                    placeholder="Chọn giọng đọc..."
                    value={selectedVoiceIds}
                    onChange={setSelectedVoiceIds}
                    optionFilterProp="children"
                    showSearch
                    filterOption={(input, option) =>
                        (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                >
                    {voiceList.map((voice: any) => (
                        <Select.Option key={voice.id} value={voice.id}>
                            {voice.name || voice.id}
                        </Select.Option>
                    ))}
                </Select>
            </Modal>
            <div className="table-area" key={refreshKey}>
                <CTable
                    tableMainTitle="Danh sách sách"
                    allowTextSearch={true}
                    onChangeInput={onChangeInput}
                    onSearch={onSearch}
                    titleOfColumnList={columns}
                    data={lstBook}
                    totalRecord={totalBookRecords}
                    onChangePagination={onChangePagination}
                    allowAddObject={true}
                    titleAddButton="Thêm sách"
                    handleAddObject={() => navigate('/management/add-book')}
                    textSearch="Tìm kiếm sách theo tên"
                    pageSize={10}
                    extraHeaderContent={
                        <Button
                            style={{ backgroundColor: '#7c3aed', color: 'white' }}
                            onClick={handleOpenBulkAction}
                        >
                            Thao tác hàng loạt
                        </Button>
                    }
                />
            </div>

            <CModalUserList
                open={isOpenModalUserBook}
                data={lstUserBook}
                totalRecord={totalUserBookRecords}
                onCancel={() => setIsOpenModalUserBook(false)}
                onChangePagination={onChangePagination}
            />

            <Modal
                title="Trạng thái xử lý audio"
                visible={isOpenModalAudio}
                onCancel={() => setIsOpenModalAudio(false)}
                footer={null}
                width={500}
            >
                <CTable
                    tableMainTitle=""
                    allowTextSearch={false}
                    onChangeInput={onChangeInput}
                    onSearch={onSearch}
                    titleOfColumnList={[
                        {
                            title: "Tên audio",
                            dataIndex: "name",
                            key: "name",
                        },
                        {
                            title: 'Trạng thái',
                            dataIndex: 'status',
                            key: 'status',
                            render: (text: any, record: any) => (
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    {record.progress !== "1.0" ?
                                        <span style={{ color: '#1890ff' }}>Đang xử lý...</span>
                                        :
                                        <span style={{ color: 'green' }}>Đã xử lý xong</span>
                                    }
                                </div>
                            )
                        },
                    ]}
                    data={lstAudioProcessing}
                    totalRecord={lstAudioProcessing.length}
                    onChangePagination={onChangePagination}
                    textSearch="Tìm kiếm audio theo tên"
                    pageSize={10}
                />
            </Modal>

            <Modal
                title="Trạng thái xử lý bản tóm tắt"
                visible={isOpenModalSummary}
                onCancel={() => setIsOpenModalSummary(false)}
                footer={null}
                width={500}
            >
                <CTable
                    tableMainTitle=""
                    allowTextSearch={false}
                    onChangeInput={onChangeInput}
                    onSearch={onSearch}
                    titleOfColumnList={[
                        {
                            title: "Tên tóm tắt",
                            dataIndex: "name",
                            key: "name",
                        },
                        {
                            title: 'Trạng thái',
                            dataIndex: 'status',
                            key: 'status',
                            render: (text: any, record: any) => (
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    {record.progress !== "1.0" ?
                                        <span style={{ color: '#1890ff' }}>Đang xử lý...</span>
                                        :
                                        <span style={{ color: 'green' }}>Đã xử lý xong</span>
                                    }
                                </div>
                            )
                        },
                    ]}
                    data={lstSummaryProcessing}
                    totalRecord={lstSummaryProcessing.length}
                    onChangePagination={onChangePagination}
                    textSearch=''
                    pageSize={10}
                />
            </Modal>

            <Modal
                title="Thao tác hàng loạt"
                open={isBulkActionVisible}
                onCancel={() => setIsBulkActionVisible(false)}
                footer={null}
                width={750}
                destroyOnClose
            >
                {bulkStep === 'select' ? (
                    <div>
                        <div style={{ marginBottom: 12, color: '#666' }}>
                            Chọn sách muốn thao tác ({bulkSelectedBookIds.length} đã chọn)
                        </div>
                        <Input
                            placeholder="Tìm sách theo tên..."
                            value={bulkBookSearch}
                            onChange={(e) => setBulkBookSearch(e.target.value)}
                            style={{ marginBottom: 12 }}
                            allowClear
                        />
                        <div style={{ marginBottom: 8 }}>
                            <Button
                                size="small"
                                onClick={() => {
                                    const filtered = bulkAllBooks.filter((b: any) =>
                                        !bulkBookSearch || (b.name || b.title || '').toLowerCase().includes(bulkBookSearch.toLowerCase())
                                    );
                                    const allIds = filtered.map((b: any) => b.id?.toString());
                                    setBulkSelectedBookIds(
                                        bulkSelectedBookIds.length === allIds.length ? [] : allIds
                                    );
                                }}
                            >
                                {bulkSelectedBookIds.length === bulkAllBooks.filter((b: any) =>
                                    !bulkBookSearch || (b.name || b.title || '').toLowerCase().includes(bulkBookSearch.toLowerCase())
                                ).length ? "Bỏ chọn tất cả" : "Chọn tất cả"}
                            </Button>
                        </div>
                        {bulkLoading ? (
                            <div style={{ padding: 32, textAlign: 'center', color: '#999' }}>Đang tải danh sách sách...</div>
                        ) : (
                            <div style={{ maxHeight: 350, overflow: 'auto' }}>
                                {bulkAllBooks
                                    .filter((b: any) =>
                                        !bulkBookSearch || (b.name || b.title || '').toLowerCase().includes(bulkBookSearch.toLowerCase())
                                    )
                                    .map((book: any) => {
                                        const bookId = book.id?.toString();
                                        const isSelected = bulkSelectedBookIds.includes(bookId);
                                        return (
                                            <div
                                                key={bookId}
                                                onClick={() => {
                                                    setBulkSelectedBookIds(prev =>
                                                        isSelected ? prev.filter(id => id !== bookId) : [...prev, bookId]
                                                    );
                                                }}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    padding: '8px 12px',
                                                    border: `1px solid ${isSelected ? '#7c3aed' : '#f0f0f0'}`,
                                                    borderRadius: 6,
                                                    marginBottom: 4,
                                                    cursor: 'pointer',
                                                    background: isSelected ? '#f5f3ff' : 'white',
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    readOnly
                                                    style={{ marginRight: 10 }}
                                                />
                                                <div style={{ fontWeight: 500 }}>{book.name || book.title}</div>
                                            </div>
                                        );
                                    })}
                            </div>
                        )}
                        <div style={{ marginTop: 16, textAlign: 'right' }}>
                            <Button onClick={() => setIsBulkActionVisible(false)} style={{ marginRight: 8 }}>Hủy</Button>
                            <Button
                                type="primary"
                                disabled={bulkSelectedBookIds.length === 0}
                                onClick={handleBulkGoToAction}
                            >
                                Tiếp tục ({bulkSelectedBookIds.length} sách)
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div>
                        <div style={{ marginBottom: 12 }}>
                            <Button size="small" onClick={() => setBulkStep('select')}>← Quay lại chọn sách</Button>
                            <span style={{ marginLeft: 8, color: '#666' }}>Đã chọn {bulkSelectedBookIds.length} sách</span>
                        </div>
                        <div style={{ marginBottom: 16 }}>
                            <div style={{ fontWeight: 600, marginBottom: 8 }}>Chọn thao tác:</div>
                            <div style={{ display: 'flex', gap: 12 }}>
                                <div
                                    onClick={() => { setBulkActionType('add'); setBulkSelectedVoiceIds([]); }}
                                    style={{
                                        flex: 1, padding: 16, border: `2px solid ${bulkActionType === 'add' ? '#1890ff' : '#f0f0f0'}`,
                                        borderRadius: 8, cursor: 'pointer', textAlign: 'center',
                                        background: bulkActionType === 'add' ? '#e6f7ff' : 'white',
                                    }}
                                >
                                    <PlusOutlined style={{ fontSize: 24, color: '#1890ff' }} />
                                    <div style={{ marginTop: 8, fontWeight: 500 }}>Thêm giọng đọc</div>
                                </div>
                                <div
                                    onClick={() => { setBulkActionType('delete'); }}
                                    style={{
                                        flex: 1, padding: 16, border: `2px solid ${bulkActionType === 'delete' ? '#ff4d4f' : '#f0f0f0'}`,
                                        borderRadius: 8, cursor: 'pointer', textAlign: 'center',
                                        background: bulkActionType === 'delete' ? '#fff1f0' : 'white',
                                    }}
                                >
                                    <DeleteOutlined style={{ fontSize: 24, color: '#ff4d4f' }} />
                                    <div style={{ marginTop: 8, fontWeight: 500 }}>Xóa giọng đọc</div>
                                </div>
                            </div>
                        </div>

                        {bulkActionType === 'add' ? (
                            <div>
                                <div style={{ color: '#666', marginBottom: 8 }}>Chọn giọng đọc muốn thêm cho {bulkSelectedBookIds.length} sách đã chọn.</div>
                                <Select
                                    mode="multiple"
                                    style={{ width: "100%" }}
                                    placeholder="Chọn giọng đọc..."
                                    value={bulkSelectedVoiceIds}
                                    onChange={setBulkSelectedVoiceIds}
                                    optionFilterProp="children"
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    {bulkVoiceList.map((voice: any) => (
                                        <Select.Option key={voice.id} value={voice.id}>
                                            {voice.name || voice.id}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </div>
                        ) : (
                            <div>
                                {bulkTasksList.length === 0 ? (
                                    <div style={{ color: '#999', padding: 16, textAlign: 'center' }}>Đang tải danh sách task...</div>
                                ) : (
                                    <div style={{ maxHeight: 300, overflow: 'auto' }}>
                                        <div style={{ marginBottom: 8 }}>
                                            <Button
                                                size="small"
                                                onClick={() => {
                                                    const allIds = bulkTasksList.map((t: any) => t.id);
                                                    setBulkSelectedTaskIds(
                                                        bulkSelectedTaskIds.length === allIds.length ? [] : allIds
                                                    );
                                                }}
                                            >
                                                {bulkSelectedTaskIds.length === bulkTasksList.length ? "Bỏ chọn tất cả" : "Chọn tất cả"}
                                            </Button>
                                        </div>
                                        {bulkTasksList.map((task: any) => {
                                            let progress = task.progress;
                                            if (typeof progress === 'string') {
                                                try { progress = JSON.parse(progress); } catch { progress = {}; }
                                            }
                                            const voiceName = task.voiceId || task.voice_name || task.id;
                                            const status = task.status || progress.status;
                                            return (
                                                <div
                                                    key={task.id}
                                                    onClick={() => {
                                                        setBulkSelectedTaskIds(prev =>
                                                            prev.includes(task.id) ? prev.filter(id => id !== task.id) : [...prev, task.id]
                                                        );
                                                    }}
                                                    style={{
                                                        display: 'flex', alignItems: 'center', padding: '8px 12px',
                                                        border: `1px solid ${bulkSelectedTaskIds.includes(task.id) ? '#ff4d4f' : '#f0f0f0'}`,
                                                        borderRadius: 6, marginBottom: 4, cursor: 'pointer',
                                                        background: bulkSelectedTaskIds.includes(task.id) ? '#fff1f0' : 'white',
                                                    }}
                                                >
                                                    <input type="checkbox" checked={bulkSelectedTaskIds.includes(task.id)} readOnly style={{ marginRight: 8 }} />
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontWeight: 500 }}>[{task.bookName}] {voiceName}</div>
                                                        <div style={{ fontSize: 12, color: '#999' }}>Status: {status}</div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        <div style={{ marginTop: 16, textAlign: 'right' }}>
                            <Button onClick={() => setIsBulkActionVisible(false)} style={{ marginRight: 8 }}>Hủy</Button>
                            <Button
                                type={bulkActionType === 'delete' ? 'primary' : 'primary'}
                                danger={bulkActionType === 'delete'}
                                disabled={
                                    (bulkActionType === 'add' && bulkSelectedVoiceIds.length === 0) ||
                                    (bulkActionType === 'delete' && bulkSelectedTaskIds.length === 0) ||
                                    bulkProcessing
                                }
                                loading={bulkProcessing}
                                onClick={handleBulkExecute}
                            >
                                {bulkActionType === 'add'
                                    ? `Tạo audio cho ${bulkSelectedBookIds.length} sách`
                                    : `Xóa ${bulkSelectedTaskIds.length} task`
                                }
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>
        </motion.div>
    );
};

export default Book;
